import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { hasPermission } from '../../lib/permissions';
import toast from 'react-hot-toast';
import {
  AcademicCapIcon,
  UserIcon,
  EnvelopeIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  TrophyIcon,
  ExclamationTriangleIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';

/** Converts option text(s) to a,b,c format for MCQ display */
function formatUserAnswerAsLetters(userAnswer, options) {
  if (!options || !Array.isArray(options) || options.length === 0) return null;
  const answers = Array.isArray(userAnswer) ? userAnswer : userAnswer ? [userAnswer] : [];
  if (answers.length === 0) return null;
  const letters = answers
    .map((text) => {
      const idx = options.findIndex((opt) => opt?.text?.trim() === String(text).trim());
      return idx >= 0 ? String.fromCharCode(97 + idx) : null;
    })
    .filter(Boolean);
  return letters.length > 0 ? letters.join(', ') : null;
}

/** Correct answer as a,b,c for MCQ; otherwise raw value */
function formatCorrectAnswerAsLetters(correctAnswer, options) {
  if (options && Array.isArray(options) && options.length > 0) {
    const letters = options
      .map((opt, idx) => (opt?.isCorrect ? String.fromCharCode(97 + idx) : null))
      .filter(Boolean);
    if (letters.length > 0) return letters.join(', ');
  }
  return correctAnswer;
}

function formatRenderableAnswer(answer) {
  if (answer == null || answer === '') return 'No answer';
  if (Array.isArray(answer)) return answer.join(', ');
  if (typeof answer === 'object') {
    if (typeof answer.code === 'string') return answer.code || 'No answer';
    if (typeof answer.text === 'string') return answer.text || 'No answer';
    return JSON.stringify(answer);
  }
  return answer;
}

const AssessmentResults = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { job, assessment } = location.state || {};

  const canResults = hasPermission(user, 'assessment.results');

  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Modal states
  const [showScoreModal, setShowScoreModal] = useState(false);
  const [showProctoringModal, setShowProctoringModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [candidateDetails, setCandidateDetails] = useState(null);
  const [proctoringDetails, setProctoringDetails] = useState(null);

  useEffect(() => {
    if (user && !canResults) {
      navigate('/dashboard', { replace: true });
      return;
    }
  }, [user, canResults, navigate]);

  useEffect(() => {
    if (assessment && canResults) {
      fetchAssessmentCandidates(assessment._id);
    } else if (!assessment) {
      navigate('/dashboard/job-post');
    }
  }, [assessment, canResults, navigate]);

  const fetchAssessmentCandidates = async (assessmentId) => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/assessments/${assessmentId}/candidates`);

      // Fetch proctoring sessions and build risk map by email
      let riskByEmail = {};
      try {
        const sessionsResp = await axios.get('/api/proctoring/sessions');
        const sessions = sessionsResp.data.sessions || [];
        const relevant = sessions.filter(
          (s) =>
            s.assessmentId?._id?.toString?.() === assessmentId.toString() ||
            s.assessmentId?.toString?.() === assessmentId.toString()
        );
        for (const s of relevant) {
          const email = s.candidateEmail;
          const score = Number(s.riskScore ?? 0);
          if (!riskByEmail[email] || score > riskByEmail[email].riskScore) {
            riskByEmail[email] = { riskScore: score };
          }
        }
      } catch (e) {
        console.warn('⚠️ Proctoring sessions fetch failed; proceeding without risk badges', e);
      }

      // Process candidates and add ranking
      const processedCandidates = response.data.candidates.map((candidate) => {
        const backendStatus = candidate.status || 'invited';
        let normalizedStatus = backendStatus;
        if (backendStatus === 'invited') normalizedStatus = 'pending';
        if (backendStatus === 'started') normalizedStatus = 'in_progress';
        if (backendStatus === 'in_progress') normalizedStatus = 'in_progress';
        if (backendStatus === 'completed') normalizedStatus = 'completed';

        const rank =
          candidate.status === 'completed' && candidate.score !== undefined
            ? response.data.candidates
                .filter((c) => c.status === 'completed' && c.score !== undefined)
                .sort((a, b) => (b.score || 0) - (a.score || 0))
                .findIndex((c) => c._id === candidate._id) + 1
            : null;

        return {
          ...candidate,
          status: normalizedStatus,
          rank,
          score: candidate.status === 'completed' ? candidate.score || 0 : 0,
          proctoringRiskScore: riskByEmail[candidate.email]?.riskScore,
        };
      });

      // Sort candidates: completed by rank, then in progress, then pending
      const sortedCandidates = processedCandidates.sort((a, b) => {
        if (a.status === 'completed' && b.status === 'completed') {
          return (a.rank || 0) - (b.rank || 0);
        }
        if (a.status === 'completed') return -1;
        if (b.status === 'completed') return 1;
        if (a.status === 'in_progress' && b.status === 'pending') return -1;
        if (a.status === 'pending' && b.status === 'in_progress') return 1;
        return 0;
      });

      setCandidates(sortedCandidates);
    } catch (error) {
      console.error('Error fetching candidates:', error);
      toast.error('Failed to load candidates');
    } finally {
      setLoading(false);
    }
  };

  const getRiskBadgeFromScore = (score) => {
    if (score === undefined || score === null || Number.isNaN(Number(score))) {
      return { label: 'No data', classes: 'bg-gray-100 text-gray-800' };
    }
    const n = Number(score);
    if (n < 50) return { label: 'Compliant', classes: 'bg-green-100 text-green-800' };
    if (n < 100) return { label: 'Flagged', classes: 'bg-orange-100 text-orange-800' };
    return { label: 'High Risk', classes: 'bg-red-100 text-red-800' };
  };
  // Points per violation type (matches server ProctoringSession.calculateRiskScore: base 5 + type bonus)
  const getViolationTypePoints = (type, count) => {
    const n = Number(count) || 0;
    const base = n * 5;
    const bonus =
      type === 'ai_assistance'
        ? n * 15
        : type === 'multiple_screen'
          ? n * 12
          : type === 'copy_paste'
            ? n * 8
            : 0;
    return base + bonus;
  };
  const getPointsPerOccurrence = (type) =>
    type === 'ai_assistance'
      ? 20
      : type === 'multiple_screen'
        ? 17
        : type === 'copy_paste'
          ? 13
          : 5;
  const formatViolationTypeLabel = (type) =>
    type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  const getDisplayRiskScore = (details) => {
    const byType = details?.violations?.byType;
    const entries = byType ? Object.entries(byType).filter(([, c]) => Number(c) > 0) : [];
    if (entries.length === 0) return details?.riskScore;
    const total = entries.reduce((s, [t, c]) => s + getViolationTypePoints(t, c), 0);
    return Math.min(total, 100);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatTime = (seconds) => {
    if (seconds === undefined || seconds === null) return '-';
    const total = Math.max(0, Math.floor(Number(seconds)));
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const secs = total % 60;
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${secs}s`;
    return `${secs}s`;
  };

  const handleViewScore = async (candidate) => {
    try {
      setSelectedCandidate(candidate);
      const response = await axios.get(
        `/api/assessments/${assessment._id}/results/${encodeURIComponent(candidate.email)}`
      );
      setCandidateDetails(response.data);
      setShowScoreModal(true);
    } catch (error) {
      const is404 = error.response?.status === 404;
      if (!is404) toast.error('Failed to load candidate details');
      setCandidateDetails(is404 ? { notAttempted: true } : null);
      setShowScoreModal(true);
    }
  };

  const handleViewProctoring = async (candidate) => {
    try {
      setSelectedCandidate(candidate);
      const response = await axios.get(`/api/proctoring/sessions`);

      const candidateSessions = response.data.sessions.filter(
        (session) =>
          session.candidateEmail === candidate.email &&
          (session.assessmentId._id?.toString() === assessment._id.toString() ||
            session.assessmentId.toString() === assessment._id.toString())
      );

      let candidateSession = candidateSessions.find((s) => s.status === 'completed');
      if (!candidateSession) {
        candidateSession = candidateSessions.find((s) => s.violations?.totalCount > 0);
      }
      if (!candidateSession) {
        candidateSession = candidateSessions.find((s) => s.events?.length > 0);
      }
      if (!candidateSession) {
        candidateSession = candidateSessions[0];
      }

      if (candidateSession) {
        setProctoringDetails(candidateSession);
      } else {
        setProctoringDetails({ notAttempted: true });
      }
      setShowProctoringModal(true);
    } catch (error) {
      console.error('Error fetching proctoring details:', error);
      toast.error('Failed to load proctoring details');
    }
  };

  const closeScoreModal = () => {
    setShowScoreModal(false);
    setSelectedCandidate(null);
    setCandidateDetails(null);
  };

  const closeProctoringModal = () => {
    setShowProctoringModal(false);
    setSelectedCandidate(null);
    setProctoringDetails(null);
  };

  const goBackToJobs = () => {
    navigate('/dashboard/job-post');
  };

  const filteredCandidates = candidates.filter((candidate) => {
    const matchesSearch =
      candidate.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      candidate.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = statusFilter === 'all' || candidate.status === statusFilter;
    return matchesSearch && matchesFilter;
  });

  if (user && !canResults) {
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <button
              onClick={goBackToJobs}
              className="flex items-center text-indigo-600 hover:text-indigo-800 transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5 mr-2" />
              Back to Results & Analytics
            </button>
            <div className="text-right">
              <h1 className="text-3xl font-bold text-gray-900">{assessment?.title}</h1>
              <p className="text-gray-600">{job?.title} • Assessment Results & Proctoring</p>
            </div>
          </div>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center">
              <UserIcon className="h-8 w-8 text-indigo-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Candidates</p>
                <p className="text-2xl font-bold text-gray-900">{candidates.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center">
              <CheckCircleIcon className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Completed</p>
                <p className="text-2xl font-bold text-gray-900">
                  {candidates.filter((c) => c.status === 'completed').length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center">
              <ClockIcon className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">In Progress</p>
                <p className="text-2xl font-bold text-gray-900">
                  {candidates.filter((c) => c.status === 'in_progress').length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-8 w-8 text-yellow-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Pending</p>
                <p className="text-2xl font-bold text-gray-900">
                  {candidates.filter((c) => c.status === 'pending').length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search candidates..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="all">All Statuses</option>
              <option value="completed">Completed</option>
              <option value="in_progress">In Progress</option>
              <option value="pending">Pending</option>
            </select>
          </div>
        </div>

        {/* Candidates Table */}
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              Candidates ({filteredCandidates.length})
            </h3>
          </div>

          {filteredCandidates.length === 0 ? (
            <div className="text-center py-12">
              <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No candidates found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm || statusFilter !== 'all'
                  ? 'Try adjusting your search or filter criteria.'
                  : 'No candidates have been invited to this assessment yet.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rank
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Candidate
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Score
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Proctoring
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Time Spent
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredCandidates.map((candidate) => (
                    <tr key={candidate._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        {candidate.rank ? (
                          <div className="flex items-center">
                            <TrophyIcon className="h-5 w-5 text-yellow-500 mr-2" />
                            <span className="font-semibold text-gray-900">#{candidate.rank}</span>
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                              <UserIcon className="h-6 w-6 text-indigo-600" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {candidate.name || 'Anonymous'}
                            </div>
                            <div className="text-sm text-gray-500 flex items-center">
                              <EnvelopeIcon className="h-4 w-4 mr-1" />
                              {candidate.email || 'No email'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(candidate.status)}`}
                        >
                          {candidate.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleViewScore(candidate)}
                          className="text-indigo-600 hover:text-indigo-900 flex items-center"
                        >
                          <EyeIcon className="h-4 w-4 mr-1" />
                          {candidate.score}/{candidate.totalPoints || 'N/A'}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleViewProctoring(candidate)}
                          className="text-indigo-600 hover:text-indigo-900 flex items-center"
                        >
                          <EyeIcon className="h-4 w-4 mr-2" />
                          {(() => {
                            const badge = getRiskBadgeFromScore(candidate.proctoringRiskScore);
                            return (
                              <span
                                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${badge.classes}`}
                              >
                                {badge.label}
                              </span>
                            );
                          })()}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatTime(candidate.timeSpent)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {candidate.completedAt ? formatDate(candidate.completedAt) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Score Details Modal */}
        {showScoreModal && selectedCandidate && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-[2px] overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-2xl font-bold text-gray-900">Candidate Results</h3>
                  <button onClick={closeScoreModal} className="text-gray-400 hover:text-gray-600">
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>

                {candidateDetails?.notAttempted ? (
                  <div className="py-10 text-center text-gray-500">
                    <p className="text-base font-semibold text-gray-600">Candidate has not attempted the assessment yet.</p>
                  </div>
                ) : candidateDetails ? (<>
                {/* Candidate Summary */}
                <div className="bg-gray-50 rounded-lg p-6 mb-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Candidate Summary</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="flex items-center text-sm text-gray-600">
                      <UserIcon className="h-4 w-4 mr-2" />
                      <span>
                        <strong>Name:</strong> {selectedCandidate.name || 'No Name'}
                      </span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <EnvelopeIcon className="h-4 w-4 mr-2" />
                      <span>
                        <strong>Email:</strong> {selectedCandidate.email}
                      </span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <ClockIcon className="h-4 w-4 mr-2" />
                      <span>
                        <strong>Time Spent:</strong> {formatTime(selectedCandidate.timeSpent)}
                      </span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <AcademicCapIcon className="h-4 w-4 mr-2" />
                      <span>
                        <strong>Score:</strong> {candidateDetails.score}/
                        {candidateDetails.totalPoints}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Performance Overview */}
                {candidateDetails.results && (
                  <>
                    <div className="bg-white rounded-lg shadow p-6 mb-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">
                        Performance Overview
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="text-center p-4 bg-gray-50 rounded-lg">
                          <div className="text-2xl font-bold text-gray-900">
                            {candidateDetails.results.length}
                          </div>
                          <div className="text-sm text-gray-600">Total Questions</div>
                        </div>
                        <div className="text-center p-4 bg-green-50 rounded-lg">
                          <div className="text-2xl font-bold text-green-700">
                            {candidateDetails.results.filter((r) => r.isCorrect).length}
                          </div>
                          <div className="text-sm text-green-600">Correct Answers</div>
                        </div>
                        <div className="text-center p-4 bg-red-50 rounded-lg">
                          <div className="text-2xl font-bold text-red-700">
                            {candidateDetails.results.filter((r) => !r.isCorrect).length}
                          </div>
                          <div className="text-sm text-red-600">Incorrect Answers</div>
                        </div>
                        <div className="text-center p-4 bg-blue-50 rounded-lg">
                          <div className="text-2xl font-bold text-blue-700">
                            {candidateDetails.results.length > 0
                              ? (
                                  (candidateDetails.results.filter((r) => r.isCorrect).length /
                                    candidateDetails.results.length) *
                                  100
                                ).toFixed(1)
                              : 0}
                            %
                          </div>
                          <div className="text-sm text-blue-600">Success Rate</div>
                        </div>
                      </div>
                    </div>

                    {/* Question-by-Question Results */}
                    <div className="bg-white rounded-lg shadow">
                      <div className="px-6 py-4 border-b border-gray-200">
                        <h4 className="text-lg font-semibold text-gray-900">Question Results</h4>
                      </div>

                      <div className="divide-y divide-gray-200">
                        {candidateDetails.results.map((result, index) => (
                          <div
                            key={index}
                            className={`p-6 ${result.isCorrect ? 'bg-green-50' : 'bg-red-50'}`}
                          >
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center space-x-3">
                                <span className="text-lg font-medium text-gray-900">
                                  Question {result.questionNumber || index + 1}
                                </span>
                                <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                                  {result.points || 1} point{(result.points || 1) !== 1 ? 's' : ''}
                                </span>
                                {result.question?.source === 'test' && (
                                  <span className="text-xs text-gray-500">
                                    From: {result.question.testTitle}
                                  </span>
                                )}
                              </div>

                              <div className="flex items-center space-x-2">
                                {result.isCorrect ? (
                                  <CheckCircleIcon className="h-5 w-5 text-green-600" />
                                ) : (
                                  <XCircleIcon className="h-5 w-5 text-red-600" />
                                )}
                                <span
                                  className={`text-sm font-medium ${
                                    result.isCorrect ? 'text-green-600' : 'text-red-600'
                                  }`}
                                >
                                  {result.earnedPoints || 0}/{result.points || 1}
                                </span>
                              </div>
                            </div>

                            <div className="mb-3">
                              <h5 className="font-medium text-gray-900 mb-1">
                                {result.question?.title || 'Question Title'}
                              </h5>
                              {result.question?.content && (
                                <p className="text-sm text-gray-600">{result.question.content}</p>
                              )}

                              {/* Show question options for multiple choice questions */}
                              {result.question?.options && result.question.options.length > 0 && (
                                <div className="mt-2">
                                  <p className="text-xs font-medium text-gray-700 mb-1">Options:</p>
                                  <div className="space-y-1">
                                    {result.question.options.map((option, optIndex) => (
                                      <div
                                        key={optIndex}
                                        className="flex items-center space-x-2 text-xs"
                                      >
                                        <span
                                          className={`w-4 h-4 rounded flex items-center justify-center ${
                                            option.isCorrect
                                              ? 'bg-green-100 text-green-800'
                                              : 'bg-gray-100 text-gray-600'
                                          }`}
                                        >
                                          {String.fromCharCode(65 + optIndex)}
                                        </span>
                                        <span
                                          className={`${
                                            option.isCorrect
                                              ? 'text-green-700 font-medium'
                                              : 'text-gray-600'
                                          }`}
                                        >
                                          {option.text}
                                          {option.isCorrect && (
                                            <span className="ml-1 text-green-600">✓</span>
                                          )}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                              <div>
                                <span className="font-medium text-gray-700">Your Answer: </span>
                                <span
                                  className={result.isCorrect ? 'text-green-600' : 'text-red-600'}
                                >
                                  {formatUserAnswerAsLetters(
                                    result.userAnswer,
                                    result.question?.options
                                  ) ??
                                    formatRenderableAnswer(result.userAnswer)}
                                </span>
                              </div>

                              <div>
                                <span className="font-medium text-gray-700">Points: </span>
                                <span className="text-gray-600">
                                  {result.earnedPoints || 0}/{result.points || 1} earned
                                </span>
                              </div>

                              {result.correctAnswer && (
                                <div className="md:col-span-2">
                                  <span className="font-medium text-gray-700">
                                    Correct Answer:{' '}
                                  </span>
                                  <span className="text-green-600">
                                    {formatCorrectAnswerAsLetters(
                                      result.correctAnswer,
                                      result.question?.options
                                    )}
                                  </span>
                                </div>
                              )}

                              {result.question?.type && (
                                <div className="md:col-span-2">
                                  <span className="font-medium text-gray-700">Question Type: </span>
                                  <span className="text-gray-600 capitalize">
                                    {result.question.type}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
                </> ) : null}

                <div className="mt-6 flex justify-end">
                  <button
                    onClick={closeScoreModal}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Proctoring Details Modal */}
        {showProctoringModal && selectedCandidate && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-[2px] overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Proctoring Session Details</h3>
                  <button
                    onClick={closeProctoringModal}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>

                <div className="space-y-4">
                  {proctoringDetails?.notAttempted ? (
                    <div className="py-10 text-center text-gray-500">
                      <p className="text-base font-semibold text-gray-600">Candidate has not attempted the assessment yet.</p>
                    </div>
                  ) : (
                  <><div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-500">Candidate</h4>
                      <p className="text-sm text-gray-900">{selectedCandidate.name}</p>
                      <p className="text-xs text-gray-500">{selectedCandidate.email}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-500">Assessment</h4>
                      <p className="text-sm text-gray-900">{assessment?.title}</p>
                    </div>
                    {proctoringDetails ? (
                      <>
                        <div>
                          <h4 className="text-sm font-medium text-gray-500">Session ID</h4>
                          <p className="text-sm text-gray-900 font-mono text-xs">
                            {proctoringDetails.sessionId}
                          </p>
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-gray-500">Status</h4>
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(proctoringDetails.status)}`}
                          >
                            {proctoringDetails.status}
                          </span>
                        </div>
                      </>
                    ) : (
                      <div className="md:col-span-2">
                        <p className="text-sm text-gray-500">
                          No proctoring data available for this candidate
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Timing Information */}
                  {proctoringDetails && (
                    <div className="border-t pt-4">
                      <h4 className="text-sm font-medium text-gray-500 mb-2">Timing</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-gray-500">Started</p>
                          <p className="text-sm text-gray-900">
                            {proctoringDetails.startedAt
                              ? formatDate(proctoringDetails.startedAt)
                              : 'Not available'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Completed</p>
                          <p className="text-sm text-gray-900">
                            {proctoringDetails.completedAt
                              ? formatDate(proctoringDetails.completedAt)
                              : 'Not available'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Duration</p>
                          <p className="text-sm text-gray-900">
                            {proctoringDetails.startedAt && proctoringDetails.completedAt
                              ? formatTime(
                                  Math.floor(
                                    (new Date(proctoringDetails.completedAt) -
                                      new Date(proctoringDetails.startedAt)) /
                                      1000
                                  )
                                )
                              : proctoringDetails.status === 'active'
                                ? 'In Progress'
                                : 'Not available'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Risk Summary</p>
                          {(() => {
                            const displayScore =
                              getDisplayRiskScore(proctoringDetails) ?? proctoringDetails.riskScore;
                            const badge = getRiskBadgeFromScore(displayScore);
                            return (
                              <span
                                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${badge.classes}`}
                              >
                                {badge.label}
                                {displayScore !== undefined ? ` · ${displayScore}/100` : ''}
                              </span>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Violations Summary */}
                  {proctoringDetails?.violations &&
                    (() => {
                      const byType = proctoringDetails.violations.byType || {};
                      const entries = Object.entries(byType).filter(
                        ([, count]) => Number(count) > 0
                      );
                      const totalPoints = entries.reduce(
                        (sum, [type, count]) => sum + getViolationTypePoints(type, count),
                        0
                      );
                      return (
                        <div className="border-t pt-4">
                          <h4 className="text-sm font-medium text-gray-500 mb-2">
                            Violations Summary
                          </h4>
                          <div className="space-y-3">
                            {entries.length > 0 && (
                              <div className="space-y-1.5">
                                {entries.map(([type, count]) => {
                                  const n = Number(count);
                                  const ptsEach = getPointsPerOccurrence(type);
                                  const subtotal = getViolationTypePoints(type, count);
                                  return (
                                    <div
                                      key={type}
                                      className="flex flex-wrap items-baseline justify-between gap-x-2 bg-gray-50 rounded-md px-3 py-2 border border-gray-200 text-xs"
                                    >
                                      <span className="font-medium text-gray-800">
                                        {formatViolationTypeLabel(type)}
                                      </span>
                                      <span className="text-gray-600">
                                        {n} × {ptsEach} pts ={' '}
                                        <strong className="text-gray-900">{subtotal}</strong>
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            <div className="flex flex-wrap items-baseline justify-between gap-x-2 rounded-md px-3 py-2 bg-gray-100 border border-gray-300 text-xs font-semibold">
                              <span className="text-gray-800">Total risk points</span>
                              <span className="text-gray-900">{totalPoints}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </> )}
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    onClick={closeProctoringModal}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssessmentResults;
